// src/hooks/useMonitoramento.js
import { useState, useEffect, useCallback } from "react";
import getCieDetails from "../API/Monitoramento/getCieDetails";

const useMonitoramento = () => {
  const [cieData, setCieData] = useState({
    events: [],
    devices: {},
    generals: {},
    lastHeartbeat: null
  });
  const [loading, setLoading] = useState(true);

  /* ──────────────────────────── Fetch CIE ──────────────────────────── */
  const fetchCieData = useCallback(async (forceLoading = true) => {
    try {
      if (forceLoading) {
        setLoading(true);
      }
      const data = await getCieDetails();
      console.log("📥 Dados CIE recebidos:", data);
      
      // Garantir que data tenha a estrutura esperada
      const formattedData = {
        events: data?.events || [],
        devices: data?.devices || {},
        generals: data?.generals || {},
        lastHeartbeat: data?.lastHeartbeat || null
      };
      
      setCieData(formattedData);
    } catch (error) {
      console.error("❌ Erro ao buscar dados CIE:", error);
      // Em caso de erro, manter estrutura válida
      setCieData({
        events: [],
        devices: {},
        generals: {},
        lastHeartbeat: null
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /* ───────────────────────────── Helpers ───────────────────────────── */
  const getDeviceStatus = useCallback((key) => {
    return cieData.devices[key]?.status || "INFO"; // default INFO
  }, [cieData.devices]);

  const formatDateTime = useCallback((d) => {
    if (!d) return "N/A";
    try {
      return new Date(d).toLocaleString("pt-BR");
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return "N/A";
    }
  }, []);

  // Devolve um objeto de alerta (FIRE | FAULT) para cada dispositivo
  const getActiveAlerts = useCallback(() => {
    if (!cieData.devices || typeof cieData.devices !== 'object') {
      return [];
    }
    
    return Object.entries(cieData.devices)
      .filter(([, d]) => ["FIRE", "FAULT"].includes((d.status || "").toUpperCase()))
      .map(([deviceKey, d]) => ({
        deviceKey,
        deviceName: d.deviceName || deviceKey,
        zoneName: d.zoneName || "Zona desconhecida",
        severity: (d.status || "INFO").toUpperCase(),  // FIRE | FAULT
        ts: d.updatedAt || new Date().toISOString(),
        message: d.lastEvent?.message || "Evento sem descrição",
        globalSeq: d.lastEvent?.globalSeq || 0,
        raw: d.lastEvent || {}
      }));
  }, [cieData.devices]);

  const getDeviceSeverity = useCallback((key) => {
    const status = getDeviceStatus(key).toUpperCase();
    if (status === "FIRE") return "fire";
    if (status === "FAULT") return "fault";
    return "normal";
  }, [getDeviceStatus]);

  // Auto-carregamento com intervalo
  useEffect(() => {
    fetchCieData();

    const interval = setInterval(() => {
      console.log("🔄 Verificando atualização de monitoramento...");
      fetchCieData(false); // não mostra loading no refresh automático
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchCieData]);

  return {
    // Dados
    cieData,
    loading,
    
    // Ações
    fetchCieData,
    
    // Helpers
    getDeviceStatus,
    formatDateTime,
    getActiveAlerts,
    getDeviceSeverity
  };
};

export default useMonitoramento;